"use client";

import React, { useState, useMemo } from "react";
import ReactDOMServer from "react-dom/server";
import Link from "next/link";
import { FiSearch } from "react-icons/fi";
import { IoMdClose } from "react-icons/io";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import Container from "../components/Container";
import Head from "next/head";

const faqs = [
  {
    category: "General",
    questions: [
      {
        question: "What is Be Underwater?",
        answer: (
          <>
            Be Underwater is an online platform where divers can
            <strong> compare and book scuba diving courses and fun dives worldwide</strong>.
            It helps you find the best dive centers, read real reviews, and book securely.
            Learn more about how it works on our{" "}
            <Link
              href="/about"
              className="text-atoll-600 font-semibold hover:underline"
            >
              About Us
            </Link>{" "}
            page.
          </>
        ),
      },
      {
        question: "How do I create a Be Underwater account?",
        answer: (
          <>
            To start booking scuba diving experiences, click the{" "}
            <strong>'Sign Up'</strong> button in the top right corner.
            Dive centers can register their business by visiting{" "}
            <Link
              href="/registercenter"
              className="text-atoll-600 font-semibold hover:underline"
            >
              Register My Center
            </Link>
            .
          </>
        ),
      },
      {
        question: "How do I log in to my Be Underwater account?",
        answer: (
          <>
            Log in anytime by clicking the{" "}
            <strong>'Log In'</strong> button in the top navigation bar.
          </>
        ),
      },
      {
        question: "How can I edit or update my Be Underwater profile?",
        answer: (
          <>
            Once you’re logged in, open the user menu in the navbar and go to{" "}
            <strong>Edit Profile</strong> to update your information.
          </>
        ),
      },
    ],
  },
  {
    category: "Using the Platform",
    questions: [
      {
        question: "How do I search for scuba diving courses or fun dives?",
        answer: (
          <>
            Use the search bar at the top of the page to find{" "}
            <strong>scuba diving courses, fun dives, or dive centers</strong> by location or certification level.
            You can also refine your search using filters or the interactive map.
          </>
        ),
      },
      {
        question: "What should I do if I forget my Be Underwater password?",
        answer: (
          <>
            Click{" "}
            <strong>'Log In'</strong> and then select{" "}
            <strong>'Forgot Password'</strong> to receive a secure reset link via email.
          </>
        ),
      },
      {
        question: "How can I report a problem or bug on Be Underwater?",
        answer: (
          <>
            Report any issue directly through the{" "}
            <strong>'Contact Us'</strong> link in the footer. Our team will review it quickly.
          </>
        ),
      },
      {
        question: "How can I share feedback or suggest new features?",
        answer: (
          <>
            We love hearing from divers and dive centers! Use the{" "}
            <strong>'Contact Us'</strong> page to share feedback, ideas, or requests.
          </>
        ),
      },
    ],
  },
  {
    category: "Privacy and Security",
    questions: [
      {
        question: "How does Be Underwater protect my personal data?",
        answer: (
          <>
            We take your privacy seriously. All personal and payment information is{" "}
            <strong>encrypted and processed using secure HTTPS protocols</strong>.
            Your data is never shared with third parties without consent.
          </>
        ),
      },
      {
        question: "How do I deactivate or delete my Be Underwater account?",
        answer: (
          <>
            Go to your{" "}
            <Link
              href="/editprofile"
              className="text-atoll-600 font-semibold hover:underline"
            >
              Edit Profile
            </Link>{" "}
            page and choose the option to deactivate your account.
          </>
        ),
      },
      {
        question: "How are payments handled on Be Underwater?",
        answer: (
          <>
            All payments are securely processed through{" "}
            <strong>Stripe</strong>, one of the world’s most trusted online payment systems.
            This ensures your scuba course bookings are always safe and protected.
          </>
        ),
      },
    ],
  },
  {
    category: "For Dive Centers",
    questions: [
      {
        question: "How can I register my dive center on Be Underwater?",
        answer: (
          <>
            Register your dive center by visiting our{" "}
            <Link
              href="/registercenter"
              className="text-atoll-600 font-semibold hover:underline"
            >
              Register My Center
            </Link>{" "}
            page and completing the quick onboarding form to start receiving bookings.
          </>
        ),
      },
      {
        question: "How do payments and commissions work for dive centers?",
        answer: (
          <>
            <p>
              Dive centers pay a small{" "}
              <strong>commission fee for each confirmed booking</strong> made through Be Underwater.
              For full details, visit our{" "}
              <Link
                href="/understandingpayout"
                className="text-atoll-600 font-semibold hover:underline"
              >
                Understanding Payouts
              </Link>{" "}
              page.
            </p>
            <p>
              To receive payments, complete your{" "}
              <strong>Stripe account setup</strong> by entering bank details in{" "}
              <strong>Edit Profile</strong>. Funds are automatically transferred after each booking.
            </p>
          </>
        ),
      },
      {
        question: "How can I add or manage my scuba diving listings?",
        answer: (
          <>
            Once logged in, dive centers can create and edit listings directly from the user menu.
            Add course details, prices, and open dates to start receiving diver bookings instantly.
          </>
        ),
      },
      {
        question: "What is Be Underwater’s cancellation policy for dive centers?",
        answer: (
          <>
            <p>
              If a dive center cancels with less than one week’s notice, the{" "}
              <strong>commission fee will still apply</strong> and divers will be fully refunded.
            </p>
            <p>
              In cases of bad weather or force majeure, Be Underwater will waive commission fees
              if valid proof is provided.
            </p>
            <p>
              <strong>No-shows:</strong> If a diver does not attend a confirmed booking,
              the dive center receives payment minus the commission fee.
            </p>
          </>
        ),
      },
    ],
  },
  {
    category: "For Divers",
    questions: [
      {
        question: "How can I register as a diver on Be Underwater?",
        answer: (
          <>
            To start booking scuba courses or fun dives, click{" "}
            <strong>'Sign Up'</strong> in the top navigation bar and create your free diver account.
          </>
        ),
      },
      {
        question: "How do I save my favorite scuba diving listings?",
        answer: (
          <>
            Click the <strong>heart icon</strong> on any dive listing to add it to your favorites.
            You can access them anytime from your user menu.
          </>
        ),
      },
      {
        question: "How do I find the best dive centers and courses?",
        answer: (
          <>
            Use the search bar to{" "}
            <strong>compare scuba diving courses, fun dives, and centers by location, rating, or price</strong>.
            You can refine results using filters or explore by map.
          </>
        ),
      },
      {
        question: "What is the Be Underwater cancellation policy for divers?",
        answer: (
          <>
            <p>
              If you cancel more than a week before your course starts,{" "}
              <strong>no payment is charged</strong>.
              Cancellations within one week of the start date are non-refundable.
            </p>
            <p>
              In case of dive center cancellation or severe weather,
              you’ll always receive a <strong>full refund</strong>.
            </p>
            <p>
              No-shows are non-refundable to ensure fairness for dive centers that prepare resources in advance.
            </p>
          </>
        ),
      },
    ],
  },
];

const FAQ = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [openQuestionIndex, setOpenQuestionIndex] = useState<{
    [key: string]: number | null;
  }>({});

  const toggleQuestion = (category: string, index: number) => {
    setOpenQuestionIndex((prev) => ({
      ...prev,
      [category]: prev[category] === index ? null : index,
    }));
  };

  const extractTextFromReactElement = (element: React.ReactNode): string => {
    const html = ReactDOMServer.renderToStaticMarkup(<>{element}</>);
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };

  const filteredFAQs = useMemo(() => {
    const lowercasedTerm = searchTerm.toLowerCase();
    return faqs
      .map((category) => ({
        ...category,
        questions: category.questions.filter(
          (q) =>
            q.question.toLowerCase().includes(lowercasedTerm) ||
            extractTextFromReactElement(q.answer)
              .toLowerCase()
              .includes(lowercasedTerm),
        ),
      }))
      .filter((category) => category.questions.length > 0);
  }, [searchTerm]);

  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": [
                {
                  "@type": "Question",
                  "name": "What is Be Underwater?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text":
                      "Be Underwater is a global platform where divers can compare and book scuba diving courses and fun dives worldwide. It connects divers with trusted dive centers and verified instructors."
                  }
                },
                {
                  "@type": "Question",
                  "name": "How do I create a Be Underwater account?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text":
                      "To create an account, click the 'Sign Up' button in the top right corner of the website. Dive centers can register through the 'Register My Center' page to start receiving bookings."
                  }
                },
                {
                  "@type": "Question",
                  "name": "How do I search for scuba diving courses or fun dives?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text":
                      "Use the search bar to find scuba diving courses and fun dives by location, certification level, or dive center. You can also use filters or the map to refine your search."
                  }
                },
                {
                  "@type": "Question",
                  "name": "How are payments handled on Be Underwater?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text":
                      "All payments are securely processed through Stripe, a trusted global payment provider. This ensures that all transactions are encrypted, safe, and reliable."
                  }
                },
                {
                  "@type": "Question",
                  "name": "How can I register my dive center on Be Underwater?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text":
                      "Dive centers can register on the 'Register My Center' page, complete the onboarding form, and start receiving bookings for scuba diving courses and fun dives."
                  }
                },
                {
                  "@type": "Question",
                  "name": "What is the cancellation policy for divers?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text":
                      "Divers can cancel up to one week before their scheduled course without being charged. If they cancel after payment has been processed, no refund will be issued. If a dive center cancels, the diver receives a full refund."
                  }
                },
                {
                  "@type": "Question",
                  "name": "How does Be Underwater protect my personal data?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text":
                      "Be Underwater uses encryption and secure HTTPS protocols to protect your data. Personal information is never shared with third parties without your consent."
                  }
                },
                {
                  "@type": "Question",
                  "name": "How can I report an issue or provide feedback?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text":
                      "You can report a bug, problem, or suggestion through the 'Contact Us' link in the footer. The Be Underwater team will review and respond promptly."
                  }
                }
              ]
            }),
          }}
        />
      </Head>

      <div className="container mx-auto px-4 py-8 sm:mt-10 mt-0">
        <Container>
          <h1 className="text-2xl font-bold mb-8">
            Frequently Asked Questions (FAQ)
          </h1>

          <div className="relative flex items-center mb-6">
            <FiSearch
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500"
              size={20}
            />
            <input
              type="text"
              placeholder="Search FAQs..."
              className="text-sm border border-gray-300 rounded-md p-2 pl-10 text-md w-full focus:outline-none focus:ring-2 focus:ring-atoll-500 bg-white-100"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                className="absolute top-1/2 transform -translate-y-1/2 right-2 text-gray-500 hover:text-gray-700 cursor-pointer"
                onClick={() => setSearchTerm("")}
              >
                <IoMdClose size={20} />
              </button>
            )}
          </div>

          {filteredFAQs.map((section) => (
            <section key={section.category} className="mb-10">
              <h2 className="text-xl font-semibold mb-6">{section.category}</h2>
              <dl className="space-y-6">
                {section.questions.map((q, index) => (
                  <div key={index}>
                    <dt
                      className="font-semibold mb-2 flex justify-between items-center cursor-pointer"
                      onClick={() => toggleQuestion(section.category, index)}
                    >
                      {q.question}
                      {openQuestionIndex[section.category] === index ? (
                        <IoIosArrowUp size={20} />
                      ) : (
                        <IoIosArrowDown size={20} />
                      )}
                    </dt>
                    {openQuestionIndex[section.category] === index && (
                      <dd className="text-sm sm:text-md leading-relaxed">
                        {q.answer}
                      </dd>
                    )}
                  </div>
                ))}
              </dl>
            </section>
          ))}
        </Container>
      </div>
    </>
  );
};

export default FAQ;